import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CreditCard, Eye, EyeOff, Globe, Image, KeyRound, Loader2, Mail, Search, Server, Sparkles, TestTube2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";

type ProviderField = {
  key: string;
  label: string;
  type?: "text" | "password" | "url" | "email";
  helper: string;
  link?: string;
};

type Provider = {
  id: string;
  name: string;
  icon: React.ElementType;
  purpose: string;
  fields: ProviderField[];
};

type CompanyIntegration = {
  id: string;
  company_id: string;
  provider: string;
  environment: "live" | "test";
  credentials: Record<string, { configured?: boolean; preview?: string | null }>;
  status: string;
  updated_at: string;
  source?: "company" | "legacy";
};

type LegacyIntegration = {
  id: string;
  slug: string;
  active_environment: "live" | "test";
  connection_status: string;
  health_status: string;
  updated_at: string;
};

type LegacyEnvironment = {
  id: string;
  integration_id: string;
  environment: "live" | "test";
  api_base_url: string | null;
  public_key: string | null;
  sender_email: string | null;
  sender_domain: string | null;
  client_id: string | null;
  status: string;
  updated_at: string;
  configuration: Record<string, unknown> | null;
};

type LegacySecretSummary = {
  integration_environment_id: string;
  key_name: string;
  is_configured: boolean;
  value_preview: string | null;
};

type LegacyDashboard = {
  integrations: LegacyIntegration[];
  environments: LegacyEnvironment[];
  secret_summaries: LegacySecretSummary[];
};

const legacyProviderMap: Record<string, { slug: string; fields: Record<string, string | string[]> }> = {
  stripe: {
    slug: "stripe",
    fields: {
      secret_key: "secret_key",
      publishable_key: ["publishable_key", "public_key"],
      webhook_secret: "webhook_secret",
    },
  },
  resend: {
    slug: "resend",
    fields: {
      api_key: "api_key",
      sender_email: "sender_email",
      sender_domain: "sender_domain",
    },
  },
  google: {
    slug: "google",
    fields: {
      client_id: "client_id",
      client_secret: "client_secret",
      analytics_property_id: "analytics_property_id",
    },
  },
  perplexity: { slug: "perplexity", fields: { api_key: "api_key" } },
  gemini: { slug: "gemini", fields: { api_key: "api_key", model: "model" } },
  unsplash: {
    slug: "media-providers",
    fields: {
      access_key: "unsplash_access_key",
      secret_key: "unsplash_secret_key",
    },
  },
  pexels: { slug: "media-providers", fields: { api_key: "pexels_api_key" } },
  pixabay: { slug: "media-providers", fields: { api_key: "pixabay_api_key" } },
  plesk: {
    slug: "plesk",
    fields: {
      base_url: "api_base_url",
      username: ["username", "client_id"],
      password: "api_key",
    },
  },
};

const providers: Provider[] = [
  {
    id: "resend",
    name: "Resend",
    icon: Mail,
    purpose: "Transactional emails, verification codes, invoices, and account messages.",
    fields: [
      { key: "api_key", label: "API key", type: "password", helper: "Used by Edge Functions to send transactional email.", link: "https://resend.com/api-keys" },
      { key: "sender_email", label: "Sender email", type: "email", helper: "The verified sender address used in outgoing emails." },
      { key: "sender_domain", label: "Sender domain", helper: "The domain verified in Resend for deliverability." },
    ],
  },
  {
    id: "stripe",
    name: "Stripe",
    icon: CreditCard,
    purpose: "Checkout, subscriptions, invoices, payment status, and billing events.",
    fields: [
      { key: "secret_key", label: "Secret key", type: "password", helper: "Server-side Stripe key used by billing functions.", link: "https://dashboard.stripe.com/apikeys" },
      { key: "publishable_key", label: "Publishable key", helper: "Browser-safe key for checkout surfaces." },
      { key: "webhook_secret", label: "Webhook signing secret", type: "password", helper: "Validates Stripe webhook payloads." },
    ],
  },
  {
    id: "google",
    name: "Google",
    icon: Globe,
    purpose: "OAuth, Analytics, Search Console, and future Google data imports.",
    fields: [
      { key: "client_id", label: "OAuth client ID", helper: "Google Cloud OAuth application client ID.", link: "https://console.cloud.google.com/apis/credentials" },
      { key: "client_secret", label: "OAuth client secret", type: "password", helper: "Server-side OAuth secret." },
      { key: "analytics_property_id", label: "Analytics property ID", helper: "GA4 property used for reporting." },
    ],
  },
  {
    id: "perplexity",
    name: "Perplexity",
    icon: Sparkles,
    purpose: "News discovery and research enrichment for automation.",
    fields: [{ key: "api_key", label: "API key", type: "password", helper: "Used by automation flows to discover and summarize sources.", link: "https://www.perplexity.ai/settings/api" }],
  },
  {
    id: "unsplash",
    name: "Unsplash",
    icon: Image,
    purpose: "Editorial image search and image import.",
    fields: [
      { key: "access_key", label: "Access key", type: "password", helper: "Used for public image search.", link: "https://unsplash.com/developers" },
      { key: "secret_key", label: "Secret key", type: "password", helper: "Used only for authenticated API operations when required." },
    ],
  },
  {
    id: "pexels",
    name: "Pexels",
    icon: Image,
    purpose: "Alternative stock image search provider.",
    fields: [{ key: "api_key", label: "API key", type: "password", helper: "Used for Pexels media search.", link: "https://www.pexels.com/api/" }],
  },
  {
    id: "pixabay",
    name: "Pixabay",
    icon: Image,
    purpose: "Alternative royalty-free media search provider.",
    fields: [{ key: "api_key", label: "API key", type: "password", helper: "Used for Pixabay media search.", link: "https://pixabay.com/api/docs/" }],
  },
  {
    id: "gemini",
    name: "Gemini",
    icon: Sparkles,
    purpose: "AI writing, translation, summarization, and image prompts.",
    fields: [
      { key: "api_key", label: "API key", type: "password", helper: "Google AI Studio key used by AI workflows.", link: "https://aistudio.google.com/app/apikey" },
      { key: "model", label: "Default model", helper: "Default generation model for this company." },
    ],
  },
  {
    id: "plesk",
    name: "Plesk",
    icon: Server,
    purpose: "Website provisioning, deployment, site sync, and hosting actions.",
    fields: [
      { key: "base_url", label: "Plesk URL", type: "url", helper: "Plesk panel API base URL." },
      { key: "username", label: "Username", helper: "Plesk API username." },
      { key: "password", label: "Password or token", type: "password", helper: "Credential used for deployment and provisioning." },
    ],
  },
];

async function invokeManager(payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("company-integrations-manager", { body: payload });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

async function invokeLegacyManager(action: string, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("integration-manager", { body: { action, ...payload } });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

function previewDirect(value: unknown) {
  if (typeof value !== "string" || value.trim() === "") return null;
  return { configured: true, preview: value.length <= 8 ? "configured" : `${value.slice(0, 4)}...${value.slice(-4)}` };
}

function legacyFieldMetadata(field: string | string[], env: LegacyEnvironment, secrets: LegacySecretSummary[]) {
  const candidates = Array.isArray(field) ? field : [field];
  const directValues: Record<string, unknown> = {
    api_base_url: env.api_base_url,
    base_url: env.api_base_url,
    public_key: env.public_key,
    publishable_key: env.public_key,
    sender_email: env.sender_email,
    sender_domain: env.sender_domain,
    client_id: env.client_id,
    ...((env.configuration && typeof env.configuration === "object") ? env.configuration : {}),
  };

  for (const key of candidates) {
    const direct = previewDirect(directValues[key]);
    if (direct) return direct;
    const secret = secrets.find((item) => item.key_name === key && item.is_configured);
    if (secret) return { configured: true, preview: secret.value_preview ?? "configured" };
  }

  return { configured: false, preview: null };
}

function mapLegacyIntegrations(data: LegacyDashboard, companyId: string, environment: "live" | "test"): CompanyIntegration[] {
  return Object.entries(legacyProviderMap).flatMap(([provider, mapping]) => {
    const integration = data.integrations?.find((item) => item.slug === mapping.slug);
    if (!integration) return [];
    const preferred = Array.from(new Set([environment, integration.active_environment, "live", "test"]));
    const env = preferred
      .map((envName) => data.environments?.find((item) => item.integration_id === integration.id && item.environment === envName))
      .find(Boolean);
    if (!env) return [];
    const secretRows = (data.secret_summaries ?? []).filter((item) => item.integration_environment_id === env.id);
    const credentials = Object.fromEntries(
      Object.entries(mapping.fields).map(([field, legacyField]) => [field, legacyFieldMetadata(legacyField, env, secretRows)]),
    );
    if (!Object.values(credentials).some((item) => item.configured)) return [];
    return [{
      id: `legacy-${provider}-${env.id}`,
      company_id: companyId,
      provider,
      environment,
      credentials,
      status: env.status ?? integration.connection_status ?? integration.health_status ?? "connected",
      updated_at: env.updated_at ?? integration.updated_at,
      source: "legacy" as const,
    }];
  });
}

function mergeIntegrations(companyRows: CompanyIntegration[], legacyRows: CompanyIntegration[]) {
  const providersWithCompanyCredentials = new Set(
    companyRows
      .filter((row) => Object.values(row.credentials ?? {}).some((field) => field?.configured))
      .map((row) => row.provider),
  );
  const fallbackRows = legacyRows.filter((row) => !providersWithCompanyCredentials.has(row.provider));
  return [...companyRows.map((row) => ({ ...row, source: row.source ?? "company" as const })), ...fallbackRows];
}

async function fetchCompanyIntegrations(companyId: string, environment: "live" | "test"): Promise<CompanyIntegration[]> {
  const [companyResult, legacyResult] = await Promise.allSettled([
    invokeManager({ action: "list", company_id: companyId, environment }),
    invokeLegacyManager("list"),
  ]);
  const companyRows = companyResult.status === "fulfilled" ? (companyResult.value.integrations ?? []) as CompanyIntegration[] : [];
  const legacyRows = legacyResult.status === "fulfilled" ? mapLegacyIntegrations(legacyResult.value as LegacyDashboard, companyId, environment) : [];
  if (companyResult.status === "rejected" && legacyResult.status === "rejected") throw companyResult.reason;
  return mergeIntegrations(companyRows, legacyRows);
}

function maskValue(value?: string) {
  return value || "Not set";
}

export default function IntegrationsPage() {
  const queryClient = useQueryClient();
  const { currentCompanyId, currentCompany, isLoading: companyLoading } = useCompany();
  const trialMode = useTrialMode();
  const [search, setSearch] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState("not_configured");
  const [environment, setEnvironment] = useState<"live" | "test">("live");

  const { data: integrations = [], isLoading } = useQuery({
    queryKey: ["company-integrations", currentCompanyId, environment],
    queryFn: () => fetchCompanyIntegrations(currentCompanyId!, environment),
    enabled: !!currentCompanyId,
  });

  const integrationMap = useMemo(() => new Map(integrations.map((item) => [item.provider, item])), [integrations]);
  const demoIntegrationMap = useMemo(() => new Map([
    ["resend", { status: "demo", credentials: { sender_email: { configured: true, preview: "news@example.com" }, sender_domain: { configured: true, preview: "example.com" } } }],
    ["stripe", { status: "demo", credentials: { publishable_key: { configured: true, preview: "pk_demo...1234" }, webhook_secret: { configured: true, preview: "whsec...demo" } } }],
    ["gemini", { status: "demo", credentials: { model: { configured: true, preview: "gemini-2.5-flash-lite" } } }],
    ["plesk", { status: "demo", credentials: { base_url: { configured: true, preview: "https://panel.example.com" }, username: { configured: true, preview: "demo-user" } } }],
  ]), []);
  const filteredProviders = providers.filter((provider) => {
    const term = search.toLowerCase();
    return !term || provider.name.toLowerCase().includes(term) || provider.purpose.toLowerCase().includes(term);
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId || !selectedProvider) throw new Error("No company or provider selected");
      if (trialMode.isReadOnly) throw new Error(TRIAL_READ_ONLY_MESSAGE);
      const credentials = Object.fromEntries(selectedProvider.fields.map((field) => [field.key, form[field.key] ?? ""]));
      await invokeManager({
        action: "save",
        company_id: currentCompanyId,
        environment,
        provider: selectedProvider.id,
        credentials,
        status,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-integrations", currentCompanyId, environment] });
      toast.success("Integration saved");
      setSelectedProvider(null);
    },
    onError: (error: Error) => toast.error(error.message || "Could not save integration"),
  });

  const testMutation = useMutation({
    mutationFn: async (providerId: string) => {
      if (!currentCompanyId) throw new Error("No company selected");
      if (trialMode.isReadOnly) throw new Error(TRIAL_READ_ONLY_MESSAGE);
      await invokeManager({ action: "test", company_id: currentCompanyId, environment, provider: providerId });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["company-integrations", currentCompanyId, environment] });
      toast.success("Connection test completed");
    },
    onError: (error: Error) => toast.error(error.message || "Connection test failed"),
  });

  const openProvider = (provider: Provider) => {
    const record = integrationMap.get(provider.id);
    setSelectedProvider(provider);
    setForm({});
    setStatus(record?.status ?? "not_configured");
    setVisible({});
  };

  if (companyLoading || isLoading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Integrations</h1>
          <p className="text-xs text-muted-foreground">
            {currentCompany?.name ?? "Company"} {environment} provider connections
            {trialMode.isReadOnly && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Demo data</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={environment} onValueChange={(value) => setEnvironment(value as "live" | "test")}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="test">Test</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Search providers" />
          </div>
        </div>
      </header>

      <main className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
        {filteredProviders.map((provider) => {
          const record = integrationMap.get(provider.id) ?? (trialMode.isReadOnly ? demoIntegrationMap.get(provider.id) as any : undefined);
          const configured = provider.fields.filter((field) => record?.credentials?.[field.key]?.configured).length;
          const Icon = provider.icon;
          return (
            <Card key={provider.id}>
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{provider.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{configured}/{provider.fields.length} fields configured</p>
                    </div>
                  </div>
                  <StatusBadge status={record?.status ?? "not_configured"} />
                </div>
                <p className="text-sm text-muted-foreground">{provider.purpose}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {provider.fields.slice(0, 3).map((field) => (
                    <div key={field.key} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{field.label}</span>
                      <span className="font-medium text-foreground">{maskValue(record?.credentials?.[field.key]?.preview ?? undefined)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => testMutation.mutate(provider.id)} disabled={testMutation.isPending || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                    <TestTube2 className="mr-2 h-4 w-4" />
                    Test
                  </Button>
                  <Button size="sm" onClick={() => openProvider(provider)}>Manage</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>

      <Dialog open={!!selectedProvider} onOpenChange={(open) => !open && setSelectedProvider(null)}>
        <DialogContent className="max-w-2xl">
          {selectedProvider && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <selectedProvider.icon className="h-5 w-5" />
                  {selectedProvider.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  {selectedProvider.purpose}
                  {trialMode.isReadOnly && <div className="mt-2 text-amber-700">{TRIAL_READ_ONLY_MESSAGE}</div>}
                </div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus} disabled={trialMode.isReadOnly}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_configured">Not configured</SelectItem>
                    <SelectItem value="connected">Connected</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
                {selectedProvider.fields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label>{field.label}</Label>
                      {field.link && <a className="text-xs text-primary hover:underline" href={field.link} target="_blank" rel="noreferrer">Where to find this key</a>}
                    </div>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type={field.type === "password" && !visible[field.key] ? "password" : field.type ?? "text"}
                        value={form[field.key] ?? ""}
                        onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                        disabled={trialMode.isReadOnly}
                        className="pl-9 pr-10"
                      />
                      {field.type === "password" && (
                        <button type="button" onClick={() => setVisible({ ...visible, [field.key]: !visible[field.key] })} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          {visible[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {field.helper} {integrationMap.get(selectedProvider.id)?.credentials?.[field.key]?.configured ? `Saved: ${integrationMap.get(selectedProvider.id)?.credentials?.[field.key]?.preview ?? "configured"}. Leave blank to keep the saved value.` : ""}
                    </p>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => selectedProvider && testMutation.mutate(selectedProvider.id)} disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>Test connection</Button>
                <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                  {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save integration
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === "connected";
  const error = status === "error";
  return (
    <Badge variant="outline" className={ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : error ? "border-red-200 bg-red-50 text-red-700" : "border-muted bg-muted text-muted-foreground"}>
      {ok ? <CheckCircle2 className="mr-1 h-3 w-3" /> : error ? <XCircle className="mr-1 h-3 w-3" /> : null}
      {status.replace(/_/g, " ")}
    </Badge>
  );
}
